/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

import React, { useEffect, useRef, useState } from 'react';
import type { FieldErrors } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { AdaptiveFormShell } from '@/shared/ui/components/form';
import { useClientDrawerForm } from './useClientDrawerForm';
import { PersonalDataSection } from './PersonalDataSection';
import { ContactsSection } from './ContactsSection';
import { ResponsibleSection } from './ResponsibleSection';
import { IdentityExtraSection } from './IdentityExtraSection';
import { AddressSection } from './AddressSection';
import { FinancialSection } from './FinancialSection';
import { PersonalInfoExtraSection } from './PersonalInfoExtraSection';
import { PreferencesSection } from './PreferencesSection';
import { OriginSection } from './OriginSection';
import { ClientDrawerSubmitButton } from './SubmitButton';
import type { ClientFormData } from './schema';
import { isDevFormBridgeEnabled } from '@/shared/utils/dev-form-bridge';
import type { ClientFormSection } from '../ClientDetailView/client-quality';

interface ClientDrawerProps {
  open: boolean;
  onClose: () => void;
  mode: 'create' | 'edit';
  clientId?: string;
  defaultValues?: Partial<ClientFormData>;
  onSuccess?: () => void;
  /**
   * The section to open onto. The registration-quality card names one so
   * a broker who clicked "Endereço · Preencher" lands on the address
   * fields rather than at the top of a seven-section form with five of
   * them collapsed.
   */
  focusSection?: ClientFormSection;
}

interface SectionState {
  origin: boolean;
  identity: boolean;
  address: boolean;
  financial: boolean;
  personalExtra: boolean;
  preferences: boolean;
}

const COLLAPSED_SECTIONS: SectionState = {
  origin: false,
  identity: false,
  address: false,
  financial: false,
  personalExtra: false,
  preferences: false,
};

/**
 * The collapsed sections, with *focus* expanded when it names one.
 *
 * `personal` and `contacts` are always expanded, so focusing them only
 * scrolls; every other section has to be opened as well.
 *
 * @param focus - The section to open onto, if any.
 * @returns The starting expanded/collapsed state.
 */
function initialSections(focus?: ClientFormSection): SectionState {
  if (!focus || focus === 'personal' || focus === 'contacts') return COLLAPSED_SECTIONS;
  return { ...COLLAPSED_SECTIONS, [focus]: true };
}

/**
 * Merges each section's current expanded state with whether it now holds a
 * validation error, so an already-expanded section stays expanded and an
 * errored section is forced open.
 *
 * @param prev - The current expanded/collapsed state per section.
 * @param withErrors - Which sections currently hold a validation error.
 * @returns The merged expanded/collapsed state.
 */
function mergeSectionExpansion(prev: SectionState, withErrors: SectionState): SectionState {
  const keys = Object.keys(prev) as Array<keyof SectionState>;
  return keys.reduce((acc, key) => {
    acc[key] = prev[key] || withErrors[key];
    return acc;
  }, {} as SectionState);
}

/** Personal-info fields that live in the collapsed "extra" section. */
const PERSONAL_EXTRA_FIELDS = [
  'nationality', 'profession', 'birthDate', 'jobTitle', 'employer', 'industry',
] as const;

/**
 * Whether any personal-info field inside the collapsed "extra" section has an error.
 */
function personalExtraHasError(errors: FieldErrors<ClientFormData>): boolean {
  const info = errors.personalInfo ?? {};
  return PERSONAL_EXTRA_FIELDS.some((key) => Boolean(info[key]));
}

/**
 * Maps form errors to which collapsible sections they live in.
 *
 * Pulled out of the component so the boolean-per-field mapping (one decision
 * point per section) stays a flat, easily-scanned function on its own,
 * instead of nested inside the `setSections` updater.
 */
function sectionsWithErrors(errors: FieldErrors<ClientFormData>): SectionState {
  return {
    origin: Boolean(errors.origin || errors.legacyCode),
    identity: Boolean(errors.identity),
    address: Boolean(errors.address),
    financial: Boolean(errors.financial),
    personalExtra: personalExtraHasError(errors),
    preferences: Boolean(errors.demandProfile),
  };
}

/**
 * Adaptive create/edit drawer for agency clients.
 *
 * @param props - Drawer open state, mode, and success callback.
 * @returns Client form shell with collapsible sections.
 */
export const ClientDrawer: React.FC<ClientDrawerProps> = ({
  open,
  onClose,
  mode,
  clientId,
  defaultValues,
  onSuccess,
  focusSection,
}) => {
  const [sections, setSections] = useState<SectionState>(() => initialSections(focusSection));
  const bodyRef = useRef<HTMLDivElement>(null);

  const toggle = (key: keyof SectionState) =>
    setSections((prev) => ({ ...prev, [key]: !prev[key] }));

  // Sections are collapsed by default, so a validation error on a field inside
  // one would otherwise be invisible: the user would only see the generic
  // "required fields" toast with nothing on screen to explain it. Expand every
  // section that actually holds an error when a submit is blocked.
  const revealSectionsWithErrors = (errors: FieldErrors<ClientFormData>) => {
    const withErrors = sectionsWithErrors(errors);
    setSections((prev) => mergeSectionExpansion(prev, withErrors));
  };

  const { form, handleSubmit, isSubmitting, resetToDefaults, agencyId } = useClientDrawerForm({
    mode,
    clientId,
    defaultValues,
    onSuccess: () => {
      onClose();
      onSuccess?.();
    },
    onValidationFailed: revealSectionsWithErrors,
  });

  // The drawer is kept mounted by its parents (only `open` toggles), so form
  // state survives a close. Reset on every open so a fresh "new client" flow
  // never inherits the previously created/edited client's values.
  const wasOpen = useRef(false);
  useEffect(() => {
    if (open && !wasOpen.current) {
      resetToDefaults();
      setSections(initialSections(focusSection));
    }
    wasOpen.current = open;
  }, [open, resetToDefaults, focusSection]);

  // After the expansion above has painted, not with it: scrolling to a
  // section still collapsed lands on its header and the fields open
  // below the fold, which is the trip this is meant to save.
  useEffect(() => {
    if (!open || !focusSection) return undefined;
    const id = window.setTimeout(() => {
      bodyRef.current
        ?.querySelector(`[data-form-section="${focusSection}"]`)
        ?.scrollIntoView({ block: 'start', behavior: 'smooth' });
    }, 0);
    return () => window.clearTimeout(id);
  }, [open, focusSection]);

  useEffect(() => {
    if (isDevFormBridgeEnabled() && open) {
      (window as any).__DEV_CLIENT_FORM__ = {
        setValue: form.setValue,
        getValues: form.getValues,
        trigger: form.trigger,
        submit: handleSubmit,
      };
    }
    return () => { (window as any).__DEV_CLIENT_FORM__ = undefined; };
  }, [open, form, handleSubmit]);

  const { t } = useTranslation('features/clients');
  const title = mode === 'edit' ? t('form.editTitle') : t('form.createTitle');
  const buttonLabel = mode === 'edit' ? t('drawer.updateButton') : t('drawer.saveButton');

  return (
    <AdaptiveFormShell
      open={open}
      onClose={onClose}
      title={title}
      density="compact"
      footer={
        <ClientDrawerSubmitButton
          onClick={handleSubmit}
          disabled={isSubmitting}
          label={buttonLabel}
          savingLabel={t('drawer.saving')}
        />
      }
    >
      <div ref={bodyRef} className="flex flex-col gap-(--space-3)">
        <div data-form-section="personal">
          <PersonalDataSection form={form} />
        </div>
        <div data-form-section="contacts">
          <ContactsSection form={form} />
        </div>
        <div data-form-section="responsible">
          <ResponsibleSection form={form} />
        </div>
        <div data-form-section="origin">
          <OriginSection
            form={form}
            mode={mode}
            collapsed={!sections.origin}
            onToggle={() => toggle('origin')}
            agencyId={agencyId}
          />
        </div>
        <div data-form-section="preferences">
          <PreferencesSection
            form={form}
            collapsed={!sections.preferences}
            onToggle={() => toggle('preferences')}
          />
        </div>
        <div data-form-section="identity">
          <IdentityExtraSection
            form={form}
            collapsed={!sections.identity}
            onToggle={() => toggle('identity')}
          />
        </div>
        <div data-form-section="personalExtra">
          <PersonalInfoExtraSection
            form={form}
            collapsed={!sections.personalExtra}
            onToggle={() => toggle('personalExtra')}
            agencyId={agencyId}
          />
        </div>
        <div data-form-section="address">
          <AddressSection
            form={form}
            collapsed={!sections.address}
            onToggle={() => toggle('address')}
          />
        </div>
        <div data-form-section="financial">
          <FinancialSection
            form={form}
            collapsed={!sections.financial}
            onToggle={() => toggle('financial')}
          />
        </div>
      </div>
    </AdaptiveFormShell>
  );
};
